import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useChatService} from '../../services';
import ContextAwareChatInterface from '../../components/GlobalChatBox/ChatInterface/ContextAwareChatInterface';
import ChatLoadingSkeleton from '../../components/GlobalChatBox/ChatLoadingSkeleton';
import {setContextInfo, setFetchedChatWithMessages, setLastActiveChatId, setShowChatbox, setIsSharedChat} from '../../redux/slices/chat';
import {HiOutlineChat} from 'react-icons/hi';

const NotFoundPage = React.lazy(() => import('../../pages/Error/404'));

interface SharedChatMessage {
  message_id: number;
  text: string;
  is_bot: boolean;
  created_at: string;
  thinking_tokens?: any[];
  interrupt?: any[];
}

interface SharedChatData {
  chat_id: number;
  title: string;
  messages: SharedChatMessage[];
  created_at: string;
}

const ShareChat = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getSharedChat } = useChatService();
  
  const [chatData, setChatData] = useState<SharedChatData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    contextInfo
  } = useSelector((state: any) => state.chatMessages);

  useEffect(() => {
    if (token) {
      dispatch(setIsSharedChat(true));
      fetchSharedChat();
    }
    dispatch(setShowChatbox(true));
    
    return () => {
      dispatch(setShowChatbox(false));
      dispatch(setIsSharedChat(false));
    };
  }, [token]);

  useEffect(() => {
    dispatch(setContextInfo({
      pageName: "Shared Chat",
      chatButtonText: "",
      headerTitle: "Shared Chat",
      welcomeMessage: "",
      contextPayload: {
        context_type: "",
        project_id: "",
        entity: "",
        entity_id: "",
        page: ""
      },
      entity_name: ""
    }))
  }, []);

  const fetchSharedChat = async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getSharedChat(token);
      setChatData(data);

      dispatch(setContextInfo({
        ...contextInfo,
        headerTitle: data.title,
        entity_name: data.title
      }))
      
      dispatch(setLastActiveChatId(data.chat_id));
      dispatch(setFetchedChatWithMessages({
        chatId: data.chat_id,
        messages: data.messages.map((msg: any) => ({
          message_id: msg.message_id,
          text: msg.text,
          is_bot: msg.is_bot,
          created_at: msg.created_at,
          thinking_tokens: msg.thinking_tokens || [],
          interrupt: msg.interrupt || []
        }))
      }));
      
    } catch (err: any) {
      console.error('Failed to fetch shared chat:', err);
      setError(err?.response?.data?.detail || 'Failed to load shared chat');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSearchInterface = () => {};
  const handleSearchChatSelect = () => {};

  if (loading) {
    return (
      <div className="fixed top-0 left-0 w-full h-full z-[9999] bg-white dark:bg-tertiary-dark">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-white dark:bg-tertiary-dark border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center">
            <div className="w-9 h-9 rounded-full bg-white dark:bg-gray-800 mr-2 flex items-center justify-center border border-gray-200 dark:border-gray-700">
              <img src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp" className="h-5 w-5" alt="Cadence Logo" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-800 dark:text-white">CadenceAI</span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400">Loading shared chat…</span>
            </div>
          </div>
          <div className="text-xs text-gray-400">Shared</div>
        </div>
        {/* Skeleton */}
        <div className="h-[calc(100%-56px)] sm:h-[calc(100%-64px)] overflow-hidden">
          <ChatLoadingSkeleton showBigChatBox={true} sidebarCollapsed={true} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed top-0 left-0 w-full h-full z-[9999] bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="w-full max-w-2xl mx-auto px-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 text-center">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <HiOutlineChat className="w-10 h-10 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Chat Not Available
            </h1>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-md transition-colors text-lg font-medium"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!chatData) {
    return <NotFoundPage />;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-full z-[9999] bg-tertiary-light dark:bg-tertiary-dark">
      <div className="w-full h-full flex flex-col">
        <div className="sticky top-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-white dark:bg-tertiary-dark text-gray-800 dark:text-white border-b border-secondary-light dark:border-secondary-dark">
          <div className="flex items-center">
            <div className="w-9 h-9 rounded-full bg-white dark:bg-gray-800 mr-2 flex items-center justify-center border border-gray-200 dark:border-gray-700">
              <img src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp" className="h-5 w-5" alt="Cadence Logo" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base sm:text-lg font-medium">CadenceAI</span>
              <span className="hidden sm:inline-block text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">Shared</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-gray-500 dark:text-gray-400 truncate max-w-[40vw]">{chatData.title}</span>
            <button
              onClick={() => navigate('/')}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full cursor-pointer text-gray-600 dark:text-gray-300"
              title="Close"
            >
              <span className="text-lg">×</span>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden bg-tertiary-light dark:bg-tertiary-dark">
          <ContextAwareChatInterface
            onCloseSearchInterface={handleCloseSearchInterface}
            onSearchChatSelect={handleSearchChatSelect}
            isSharedChat={true}
            readOnly={true}
          />
        </div>

        <div className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 sm:p-4 flex items-center justify-between">
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">Want to continue this conversation?</div>
          <a
            href="/"
            className="px-4 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white"
          >
            Sign in
          </a>
        </div>
      </div>
    </div>
  );
};

export default ShareChat;
